/* eslint-disable react-hooks/exhaustive-deps */
/**
 * BrandAssetGallery — Phase 1.2.7 admin triage UI (refactored May 2026).
 *
 * Lets operators paginate, filter, and bulk-update the brand-asset pool
 * that the SharePoint ingestion worker keeps populated. Backed by the
 * /brand/gallery + /brand/assets endpoints in brand_admin_v13_routes.py.
 *
 * Refactor note (May 2026): the original 1,798-line monolith is now
 * split into colocated components under components/gallery/. This file
 * is now the page shell: state orchestration, fetching, URL sync,
 * routing of children. All sub-components preserve their `data-testid`
 * attributes so existing test selectors remain valid.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { RotateCw, Camera } from "lucide-react";

import { API, PAGE_SIZE, authedFetch } from "../components/gallery/utils";
import FilterPanel from "../components/gallery/FilterPanel";
import AssetCard from "../components/gallery/AssetCard";
import BulkActionBar from "../components/gallery/BulkActionBar";
import AssetDetailModal from "../components/gallery/AssetDetailModal";
import TriageBanner from "../components/gallery/TriageBanner";
import PoolAwarenessPanel from "../components/gallery/PoolAwarenessPanel";


export default function BrandAssetGallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId =
    searchParams.get("ws") || "a24c8722-c708-4511-a5cc-8b98a884bbfb";

  const [filters, setFilters] = useState(() => ({
    _ws: workspaceId,
    q: searchParams.get("q") || "",
    origin: searchParams.get("origin") || "",
    pool: searchParams.get("pool") || "",
    is_active: searchParams.get("is_active") || "all",
    source_folder: searchParams.get("source_folder") || "",
    // Phase 1.4 filters
    classifier_verdict: searchParams.get("classifier_verdict") || "",
    publication_status: searchParams.get("publication_status") || "",
    classification_state: searchParams.get("classification_state") || "",
    page: parseInt(searchParams.get("page") || "1", 10),
  }));

  const [data, setData] = useState({ items: [], total: 0, facets: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailAsset, setDetailAsset] = useState(null);
  const [debouncedQ, setDebouncedQ] = useState(filters.q);

  // Debounce q (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  const fetchUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.origin) p.set("origin", filters.origin);
    if (filters.pool) p.set("pool", filters.pool);
    if (filters.is_active && filters.is_active !== "all")
      p.set("is_active", filters.is_active);
    if (filters.source_folder) p.set("source_folder", filters.source_folder);
    if (debouncedQ) p.set("q", debouncedQ);
    if (filters.classifier_verdict) p.set("classifier_verdict", filters.classifier_verdict);
    if (filters.publication_status) p.set("publication_status", filters.publication_status);
    if (filters.classification_state) p.set("classification_state", filters.classification_state);
    p.set("page", String(filters.page || 1));
    p.set("per_page", String(PAGE_SIZE));
    return `${API}/v2/workspaces/${workspaceId}/brand/gallery?${p.toString()}`;
  }, [filters.origin, filters.pool, filters.is_active, filters.source_folder, debouncedQ,
      filters.classifier_verdict, filters.publication_status, filters.classification_state,
      filters.page, workspaceId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authedFetch(fetchUrl);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
    } catch (e) {
      setError(e.message || "Failed to load gallery");
    } finally {
      setLoading(false);
    }
  }, [fetchUrl]);

  useEffect(() => {
    load();
  }, [load]);

  // Sync filters to URL (skip the _ws sentinel)
  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (k.startsWith("_")) return;
      if (v === "" || v === null || v === undefined) return;
      if (k === "is_active" && v === "all") return;
      if (k === "page" && (!v || v === 1)) return;
      p.set(k, String(v));
    });
    setSearchParams(p, { replace: true });
  }, [filters]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  }, []);

  const onPageChange = (p) => setFilters((f) => ({ ...f, page: p }));
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const onAssetUpdated = (updated) => {
    setData((d) => ({
      ...d,
      items: d.items.map((i) =>
        i.asset_id === updated.asset_id ? { ...i, ...updated } : i
      ),
    }));
  };

  const resetFilters = () =>
    setFilters({
      _ws: workspaceId,
      q: "",
      origin: "",
      pool: "",
      is_active: "all",
      source_folder: "",
      classifier_verdict: "",
      publication_status: "",
      classification_state: "",
      page: 1,
    });

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-[1600px]" data-testid="brand-asset-gallery-page">
      <div className="flex items-baseline justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Brand asset gallery</h1>
          <p className="text-sm text-slate-500">
            Triage the SharePoint-ingested pool. Activate, reassign pool, or flag origin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="gallery-total">
            {loading ? "…" : `${data.total} assets`}
          </Badge>
          <Link to="/admin/brand-assets/stock-search">
            <Button variant="outline" size="sm" data-testid="open-stock-search-btn">
              <Camera className="h-4 w-4 mr-1" /> Search stock library
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={load} data-testid="gallery-refresh">
            <RotateCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <FilterPanel
          filters={filters}
          facets={data.facets}
          onChange={(next) => setFilters({ ...next, _ws: workspaceId })}
          onReset={resetFilters}
          workspaceId={workspaceId}
          onConsentChange={load}
        />

        <main className="flex-1 min-w-0">
          <PoolAwarenessPanel workspaceId={workspaceId} />
          <TriageBanner
            workspaceId={workspaceId}
            onJumpToFilter={(patch) =>
              setFilters((f) => ({
                ...f,
                // Clear competing filter keys before applying so a user
                // clicking "Unsafe" doesn't keep an old verdict filter.
                classifier_verdict: "",
                classification_state: "",
                ...patch,
                page: 1,
              }))
            }
          />

          {error && (
            <div
              className="rounded border border-red-200 bg-red-50 text-red-800 p-3 mb-4 text-sm"
              data-testid="gallery-error"
            >
              {error}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full rounded-lg" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div
              className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg text-slate-500"
              data-testid="gallery-empty"
            >
              No assets match the current filters.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="gallery-grid">
                {data.items.map((asset) => (
                  <AssetCard
                    key={asset.asset_id}
                    asset={asset}
                    selected={selectedIds.includes(asset.asset_id)}
                    onSelect={toggleSelect}
                    onOpen={setDetailAsset}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between mt-6 text-sm" data-testid="gallery-pagination">
                <div className="text-slate-500">
                  Page {filters.page} of {totalPages} · showing{" "}
                  {(filters.page - 1) * PAGE_SIZE + 1}–
                  {Math.min(filters.page * PAGE_SIZE, data.total)} of {data.total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={filters.page <= 1}
                    onClick={() => onPageChange(filters.page - 1)}
                    data-testid="gallery-page-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={!data.has_more}
                    onClick={() => onPageChange(filters.page + 1)}
                    data-testid="gallery-page-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      <BulkActionBar
        selectedIds={selectedIds}
        totalMatching={data.total}
        filters={filters}
        onClear={() => setSelectedIds([])}
        onApplied={() => {
          setSelectedIds([]);
          load();
        }}
      />

      {detailAsset && (
        <AssetDetailModal
          asset={detailAsset}
          workspaceId={workspaceId}
          onClose={() => setDetailAsset(null)}
          onUpdated={onAssetUpdated}
        />
      )}
    </div>
  );
}
